import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Switch } from '@shared/ui/atoms/Switch';
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { selectConfig, updateConfig } from '@shared/store/config.duck';
import { failureNotification } from '@shared/store/notification.duck';
import { ConfigEntity } from '@shared/services/ConfigService';
import { ConfigurationSection, SpaceBetweenRow } from '../../StyleComponents';

// Half-hourly from 07:00 to 14:00
const DEADLINE_TIMES = Array.from({ length: 15 }, (_, i) => {
  const minutes = 7 * 60 + i * 30;
  const label = `${Math.floor(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
  return { id: label, name: label };
});

const NUDGE_LEAD_TIMES = [
  { id: '15', name: '15 minutes before' },
  { id: '30', name: '30 minutes before' },
  { id: '60', name: '1 hour before' },
];

/**
 * What Team Today does when someone checks in, or does not. "Update whereabouts", "Mark bookings as confirmed" and
 * who sees confirmed bookings are saved to the company config as soon as they change. The settings tagged Preview
 * only live in this component until the API for them exists.
 */
export function AutomaticCheckinActionSettings() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const config = useSelector(selectConfig);
  const waypointScreensOn = !!user?.companyEntity?.signageEnabled;

  const [updateWhereaboutsOffice, setUpdateWhereaboutsOffice] = useState(false);
  const [markBookingsConfirmed, setMarkBookingsConfirmed] = useState(false);
  const [confirmedVisibleToEveryone, setConfirmedVisibleToEveryone] = useState(false);
  useEffect(() => {
    setUpdateWhereaboutsOffice(!!config?.checkinUpdateWhereaboutsEnabled);
    setMarkBookingsConfirmed(!!config?.checkinConfirmBookingsEnabled);
    setConfirmedVisibleToEveryone(!!config?.checkinConfirmedVisibleToEveryone);
  }, [config?.checkinUpdateWhereaboutsEnabled, config?.checkinConfirmBookingsEnabled, config?.checkinConfirmedVisibleToEveryone]);

  // Shows the change straight away, and puts it back if the save fails
  const saveConfig = async (changes: Partial<ConfigEntity>, revert: () => void) => {
    if (!config) return;
    const action = await dispatch(updateConfig({ ...config, ...changes }));
    if ((action as any)?.meta?.requestStatus === 'rejected') {
      revert();
      dispatch(failureNotification('Could not save this setting. Please try again.'));
    }
  };

  const onUpdateWhereaboutsOfficeChange = (enabled: boolean) => {
    setUpdateWhereaboutsOffice(enabled);
    saveConfig({ checkinUpdateWhereaboutsEnabled: enabled }, () => setUpdateWhereaboutsOffice(!enabled));
  };

  const onMarkBookingsConfirmedChange = (enabled: boolean) => {
    setMarkBookingsConfirmed(enabled);
    saveConfig({ checkinConfirmBookingsEnabled: enabled }, () => setMarkBookingsConfirmed(!enabled));
  };

  const onConfirmedVisibleToEveryoneChange = (enabled: boolean) => {
    setConfirmedVisibleToEveryone(enabled);
    saveConfig({ checkinConfirmedVisibleToEveryone: enabled }, () => setConfirmedVisibleToEveryone(!enabled));
  };

  const [showLivePresence, setShowLivePresence] = useState(false);
  const [resetIfNotIn, setResetIfNotIn] = useState(false);
  const [deadline, setDeadline] = useState('10:30');
  const [nudgeInTeams, setNudgeInTeams] = useState(false);
  const [nudgeLeadMinutes, setNudgeLeadMinutes] = useState('30');

  return (
    <>
      <PreviewNotice>
        <PreviewTag>Preview</PreviewTag>
        <BodyRegular>Settings marked Preview are not saved yet. They show what automatic check-in will be able to do.</BodyRegular>
      </PreviewNotice>

      <GroupTitle weight={600}>When someone checks in</GroupTitle>

      <ConfigurationSection>
        <SpaceBetweenRow style={{ marginBottom: 2 }}>
          <BodyLarge colour={Colours.blue}>Update whereabouts to the office they checked into</BodyLarge>
          <Switch value={updateWhereaboutsOffice} label="" onChange={onUpdateWhereaboutsOfficeChange} />
        </SpaceBetweenRow>
        <BodyRegular>
          If someone checks in but their whereabouts for the day say something else, such as working from home,
          another office or nothing at all, they are put into the office they checked into. They are not given a desk
          or an area. A check-in before midday changes the whole day, and a later one changes the afternoon. Anyone
          already down as in that office is left as they are.
        </BodyRegular>
        <Warning>
          <BodyRegular weight={600}>Warning: this may override booked leave.</BodyRegular>
        </Warning>
      </ConfigurationSection>

      <ConfigurationSection>
        <SpaceBetweenRow style={{ marginBottom: 2 }}>
          <BodyLarge colour={Colours.blue}>Mark bookings as confirmed</BodyLarge>
          <Switch value={markBookingsConfirmed} label="" onChange={onMarkBookingsConfirmedChange} />
        </SpaceBetweenRow>
        <BodyRegular>
          Office days that someone has checked in on show a green tick in whereabouts, which covers any desk or
          parking they booked for the day. Hover over the tick to see when they checked in.
        </BodyRegular>

        {markBookingsConfirmed && (
          <NestedSetting>
            <SpaceBetweenRow style={{ marginBottom: 2 }}>
              <BodyRegular weight={600}>Show confirmed bookings to everyone</BodyRegular>
              <Switch value={confirmedVisibleToEveryone} label="" onChange={onConfirmedVisibleToEveryoneChange} />
            </SpaceBetweenRow>
            <BodyRegular>
              When this is off, only company admins and office admins can see which bookings are confirmed.
            </BodyRegular>
          </NestedSetting>
        )}
      </ConfigurationSection>

      <ConfigurationSection>
        <SpaceBetweenRow style={{ marginBottom: 2 }}>
          <TitleRow><BodyLarge colour={Colours.blue}>Show who is in on Waypoint screens</BodyLarge><PreviewTag>Preview</PreviewTag></TitleRow>
          <Switch value={showLivePresence} label="" onChange={setShowLivePresence} />
        </SpaceBetweenRow>
        <BodyRegular>
          Waypoint screens show people who have checked in as in the office now, separately from people who are only
          expected.
        </BodyRegular>
        {showLivePresence && !waypointScreensOn && (
          <BodyRegular colour={Colours.darkGrey} style={{ marginTop: 8 }}>
            Waypoint screens are switched off for your company. Switch them on under Other to use this.
          </BodyRegular>
        )}
      </ConfigurationSection>

      <GroupTitle weight={600}>When someone does not check in</GroupTitle>

      <ConfigurationSection>
        <SpaceBetweenRow style={{ marginBottom: 2 }}>
          <TitleRow><BodyLarge colour={Colours.blue}>Reset whereabouts and release desks</BodyLarge><PreviewTag>Preview</PreviewTag></TitleRow>
          <Switch value={resetIfNotIn} label="" onChange={setResetIfNotIn} />
        </SpaceBetweenRow>
        <BodyRegular style={{ marginBottom: resetIfNotIn ? 16 : 0 }}>
          If someone is down as in the office but has not checked in by the time below, their whereabouts for the day
          change to Unknown and their desk is released for others to book.
        </BodyRegular>

        {resetIfNotIn && (
          <>
            <SpaceBetweenRow>
              <BodyRegular weight={600} style={{ whiteSpace: 'nowrap', marginRight: 16 }}>Check in by</BodyRegular>
              <GenericDropDownList className={'dropdown'}
                                   onSelect={setDeadline}
                                   items={DEADLINE_TIMES}
                                   placeholder={'Check in by'}
                                   selectedId={deadline} />
            </SpaceBetweenRow>
            <BodyRegular colour={Colours.darkGrey}>In each office's local time.</BodyRegular>

            <NestedSetting>
              <SpaceBetweenRow style={{ marginBottom: 2 }}>
                <BodyRegular weight={600}>Remind them in Microsoft Teams first</BodyRegular>
                <Switch value={nudgeInTeams} label="" onChange={setNudgeInTeams} />
              </SpaceBetweenRow>
              <BodyRegular style={{ marginBottom: nudgeInTeams ? 16 : 0 }}>
                Send the person a Teams message before their whereabouts are reset and their desk is released. Only
                people with the Team Today app in Teams get it.
              </BodyRegular>
              {nudgeInTeams && (
                <SpaceBetweenRow>
                  <BodyRegular weight={600} style={{ whiteSpace: 'nowrap', marginRight: 16 }}>Send it</BodyRegular>
                  <GenericDropDownList className={'dropdown'}
                                       onSelect={setNudgeLeadMinutes}
                                       items={NUDGE_LEAD_TIMES}
                                       placeholder={'Send it'}
                                       selectedId={nudgeLeadMinutes} />
                </SpaceBetweenRow>
              )}
            </NestedSetting>
          </>
        )}
      </ConfigurationSection>
    </>
  );
}

const PreviewNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: ${Colours.veryLightGrey};
  border-radius: 8px;
`;

const PreviewTag = styled.span`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${Colours.orange20};
  color: ${Colours.darkGrey};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const GroupTitle = styled(BodyRegular)`
  margin: 8px 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 13px;
  color: ${Colours.darkGrey};
`;

const Warning = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  background: ${Colours.orange10};
  border-left: 3px solid ${Colours.orange};
  border-radius: 0 8px 8px 0;
`;

const NestedSetting = styled.div`
  margin-top: 16px;
  padding: 16px;
  border-left: 3px solid ${Colours.blue20};
  background: ${Colours.blue02};
  border-radius: 0 8px 8px 0;
`;
